import {
  CircularProgress,
  TextField,
  Zoom,
} from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import CustomAddModal from "../../../components/Common/CustomAddModal";
import { toastError, toastSuccess, toastWarning } from "../../../utils";
import CargoList from "../Calculator/Contracts/cargoList";
import {
  getAllCargoContract,
  getCargoContractById,
  updateCargoContract,
} from "../Calculator/actions";
import { createCargoContract } from "../Orders/actions";
import CreateCargoContractForm from "../Orders/createCargoContractModel";
import { createNextTrip } from "./action";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

const TRIP_TYPES = [
  {
    value: "vessel-cargo",
    label: "Vessel - Cargo",
    description: "Pick an open Cargo Contract for the next voyage, then enter the estimate details.",
  },
  {
    value: "vessel-vessel",
    label: "Vessel - Vessel",
    description: "Copies the current Vessel Contract as Vessel Out, then goes straight to the estimate details.",
  },
];

const NextTripCargoSelectionModal = ({
  open,
  onClose,
  previousOperationId,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [tripType, setTripType] = useState("vessel-cargo");
  const isVesselVessel = tripType === "vessel-vessel";
  // Vessel-Vessel copies the current vessel contract as Vessel Out, so it has no contract-selection step
  const steps = isVesselVessel
    ? ["Trip Type", "Estimate Details"]
    : ["Trip Type", "Select Cargo Contract", "Estimate Details"];
  const currentStep = steps[activeStep];
  const isCargoStep = currentStep === "Select Cargo Contract";
  const [selectedCargo, setSelectedCargo] = useState(null);
  const [cargoList, setCargoList] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cargoFilters, setCargoFilters] = useState({
    offset: 1,
    limit: 10,
    totalPages: 1,
    totalItems: 0,
  });
  const [estimateData, setEstimateData] = useState({
    contractName: "",
    comments: "",
  });

  // Cargo Contract View / Edit Modal State (Image 3)
  const [viewCargoModalOpen, setViewCargoModalOpen] = useState(false);
  const [cargoFormData, setCargoFormData] = useState({});
  const [cargoTextContract, setCargoTextContract] = useState(null);
  const [cargoAttachments, setCargoAttachments] = useState([]);
  const [prevAttachments, setPrevAttachments] = useState({
    attachments: [],
    textContract: null,
  });

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setTripType("vessel-cargo");
      setSelectedCargo(null);
      setEstimateData({ contractName: "", comments: "" });
      setCargoFilters((prev) => ({ ...prev, offset: 1 }));
    } else {
      setCargoFilters((prev) => ({ ...prev, offset: 1 }));
    }
  }, [open]);

  useEffect(() => {
    if (open && isCargoStep) loadCargoContracts();
  }, [open, cargoFilters.offset, isCargoStep]);

  const handleTripTypeChange = (e) => {
    setTripType(e.target.value);
    setSelectedCargo(null);
    setCargoList([]);
    setCargoFilters((prev) => ({ ...prev, offset: 1 }));
  };

  const loadCargoContracts = async () => {
    setIsListLoading(true);
    try {
      const response = await getAllCargoContract(
        {},
        {
          page: cargoFilters.offset,
          limit: cargoFilters.limit,
          access: "private",
          status: "open",
        }
      );
      const list = Array.isArray(response?.data) ? response.data : [];
      setCargoList(list);
      setCargoFilters((prev) => ({
        ...prev,
        totalItems: response?.totalItems || 0,
        totalPages: response?.totalPages || 1,
      }));
    } catch (err) {
      console.error("Error loading cargo contracts:", err);
      toastError("Failed to load cargo contracts");
    } finally {
      setIsListLoading(false);
    }
  };

  const handleCargoSelect = (cargo) => {
    setSelectedCargo(cargo);
    if (cargo) {
      const name = cargo.contractName || cargo.name || cargo.cargoDetails?.name || "Next Trip Estimate";
      setEstimateData((prev) => ({
        ...prev,
        contractName: prev.contractName || `${name} - Trip`,
      }));
    }
  };

  const handleNext = () => {
    if (isCargoStep && !selectedCargo) {
      toastWarning("Please select a Cargo Contract to continue");
      return;
    }
    if (currentStep === "Trip Type" && isVesselVessel) {
      setEstimateData((prev) => ({
        ...prev,
        contractName: prev.contractName || "Vessel-Vessel Trip",
      }));
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEstimateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // View / Edit Cargo Contract Handler
  const handleViewCargoContract = async (cargo) => {
    const targetId = cargo?._id;
    if (!targetId) return;

    try {
      const fullDataRes = await getCargoContractById(targetId);
      const fullData = fullDataRes.data || fullDataRes;
      const customerCargoObj =
        fullData?.cargoDetails?.customerCargo ||
        fullData?.cargoDetails ||
        fullData?.customerCargo ||
        fullData;

      const list = customerCargoObj?.cargoList || fullData?.cargoList || [];

      setCargoFormData({
        ...fullData,
        ...customerCargoObj,
        _id: targetId,
        cargoList: list,
      });
      setPrevAttachments({
        attachments: [...(fullData?.attachments || [])],
        textContract: fullData?.textContract,
      });
      setCargoTextContract(null);
      setCargoAttachments([]);
      setViewCargoModalOpen(true);
    } catch (err) {
      console.error("Error loading cargo contract details:", err);
      toastError("Failed to fetch full cargo contract details");
    }
  };

  const handleCargoFormInputChange = (e) => {
    const { name, value } = e.target;
    setCargoFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCargoTextContractUpload = (e) => {
    if (e.target.files[0]) {
      setCargoTextContract(e.target.files[0]);
    }
  };

  const handleCargoAttachmentUpload = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter(
        (file) => file.size <= 5 * 1024 * 1024
      );
      if (validFiles.length !== newFiles.length) {
        toastWarning("Some files exceed the 5 MB limit and were not added.");
      }
      setCargoAttachments((prev) => [...(Array.isArray(prev) ? prev : []), ...validFiles]);
    }
  };

  const removeCargoAttachment = (index) => {
    setCargoAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateCargoContract = async () => {
    if (!cargoFormData?._id) return;
    try {
      const { _id, cargoList: cList, actualCargoContract, operationCargoContract, ...payload } = cargoFormData;
      const cpDate = payload.cpDate || payload.contractDate || payload.cp_date || moment().format("YYYY-MM-DD");
      const cargoKey = payload?.cargoDetails?.customerCargo
        ? "customerCargo"
        : payload?.cargoDetails?.clientQuery
          ? "clientQuery"
          : null;

      const finalData = {
        ...payload,
        cpDate,
        cargoDetails: cargoKey
          ? {
              ...payload.cargoDetails,
              [cargoKey]: {
                ...payload.cargoDetails[cargoKey],
                cargoList: cList || [],
              },
            }
          : {
              ...payload.cargoDetails,
              cargoList: cList || [],
            },
      };

      if (!finalData.contractName) {
        toastError("Contract Name is required");
        return;
      }

      const formPayload = new FormData();
      if (cargoTextContract) {
        const fileType = cargoTextContract.type || "application/octet-stream";
        const fileName = cargoTextContract.name || "contract";
        const textBlob = new Blob([cargoTextContract], { type: fileType });
        const textFile = new File([textBlob], fileName, { type: fileType });
        formPayload.append("files", textFile);
        formPayload.append("textContractMetadata", JSON.stringify({ fileType: "textContract" }));
      }

      if (cargoAttachments?.length) {
        cargoAttachments.forEach((file) => {
          formPayload.append("files", file);
        });
      }

      formPayload.append(
        "data",
        JSON.stringify({
          ...finalData,
          attachments: prevAttachments?.attachments || [],
          textContract: prevAttachments?.textContract || null,
        })
      );

      await updateCargoContract({ id: _id }, formPayload);
      toastSuccess("Cargo contract updated successfully");
      setViewCargoModalOpen(false);
      loadCargoContracts();
    } catch (err) {
      console.error("Error updating cargo contract:", err);
      toastError(err?.response?.data?.message || err?.message || "Failed to update cargo contract");
    }
  };

  const handleCopyCargoContract = async () => {
    if (!cargoFormData?._id) return;
    try {
      const { _id, createdAt, updatedAt, estId, ...restData } = cargoFormData;
      await createCargoContract({
        ...restData,
        status: "open",
        contractName: `${restData.contractName || "Contract"} (Copy)`,
      });
      toastSuccess("Contract copied successfully");
      setViewCargoModalOpen(false);
      loadCargoContracts();
    } catch (err) {
      console.error("Error copying cargo contract:", err);
      toastError("Failed to copy cargo contract");
    }
  };

  const handleSubmit = async () => {
    if (!isVesselVessel && !selectedCargo?._id) {
      toastError("Cargo Contract selection is required");
      return;
    }
    if (!estimateData.contractName.trim()) {
      toastError("Contract Name is required");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        previousOperationId,
        ...(isVesselVessel
          ? { tripType }
          : { cargoContractId: selectedCargo._id }),
        contractName: estimateData.contractName.trim(),
        comments: estimateData.comments,
      };

      const res = await createNextTrip(payload);
      const createdEstimate = res?.data;
      if (createdEstimate?._id) {
        toastSuccess(res?.message || "Next Trip Estimate created successfully");
        if (typeof onSuccess === "function") {
          await onSuccess(createdEstimate);
        }
        onClose();
      } else {
        toastError(res?.message || "Next Trip was created but its response was incomplete");
      }
    } catch (err) {
      console.error("Error creating next trip:", err);
      toastError(err?.response?.data?.message || err?.data?.message || err?.message || "Failed to create Next Trip");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={isCargoStep ? "lg" : "sm"}
        TransitionComponent={Transition}
        classes={{
          paper: "next-trip-modal-compact rounded-none overflow-hidden border border-slate-200 shadow-2xl",
        }}
      >
        {/* Compact Header Bar — title + close only */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '6px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <span style={{ color: '#313C4C', fontSize: 14, fontWeight: 500, letterSpacing: '0.025em' }}>
            Create Next Trip{isCargoStep ? " (Cargo Contract Selection)" : ""}
          </span>
          <button
            onClick={onClose}
            disabled={submitting}
            aria-label="Close Next Trip dialog"
            style={{ padding: 4, color: '#64748b', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title="Close"
          >
            <MdClose size={16} />
          </button>
        </div>

        {/* Scoped compact styles for MUI table cells inside this modal */}
        <style>{`
          .next-trip-modal-compact .MuiDialogContent-root {
            padding: 8px 10px !important;
          }
          .next-trip-modal-compact .MuiDialogContent-dividers {
            border-top: none;
            border-bottom: none;
          }
          .next-trip-modal-compact .MuiTableCell-root {
            padding: 4px 8px !important;
            font-size: 12px !important;
            line-height: 1.3 !important;
          }
          .next-trip-modal-compact .MuiTableCell-head {
            padding: 5px 8px !important;
            font-size: 11px !important;
            font-weight: 600 !important;
            background: #e2e8f0 !important;
            color: #1e293b !important;
          }
          .next-trip-modal-compact .MuiTableRow-root {
            height: 28px !important;
          }
          .next-trip-modal-compact .MuiCheckbox-root {
            padding: 2px !important;
          }
          .next-trip-modal-compact .MuiTableContainer-root {
            max-height: 320px !important;
          }
        `}</style>

        {/* Content */}
        <DialogContent
          dividers
          className="scroll-thin bg-white"
          style={{ padding: '8px 10px', overflowY: 'auto' }}
        >
          {/* Compact Step Indicator */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '4px 0 8px 0', borderBottom: '1px solid #f1f5f9', marginBottom: 8 }}>
            {steps.map((label, idx) => (
              <React.Fragment key={label}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 18, height: 18, borderRadius: '50%', fontSize: 10, fontWeight: 500,
                      border: '1px solid',
                      ...(idx <= activeStep
                        ? { background: '#313C4C', color: '#fff', borderColor: '#313C4C' }
                        : { background: '#fff', color: '#94a3b8', borderColor: '#cbd5e1' })
                    }}
                  >
                    {idx + 1}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      ...(idx === activeStep
                        ? { color: '#334155', fontWeight: 500 }
                        : { color: '#94a3b8', fontWeight: 500 })
                    }}
                  >
                    {label}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div style={{ width: 40, height: 1, background: idx < activeStep ? '#313C4C' : '#cbd5e1' }} />
                )}
              </React.Fragment>
            ))}
          </div>

          {currentStep === "Trip Type" && (
            <div style={{ maxWidth: 560, margin: '0 auto', padding: '4px 0' }}>
              <p style={{ fontSize: 12, color: '#334155', fontWeight: 500, margin: '0 0 8px 0' }}>
                What kind of trip do you want to create next?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {TRIP_TYPES.map((t) => {
                  const active = tripType === t.value;
                  return (
                    <label
                      key={t.value}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', cursor: 'pointer',
                        border: `1px solid ${active ? '#313C4C' : '#cbd5e1'}`,
                        background: active ? '#f1f5f9' : '#fff',
                      }}
                    >
                      <input
                        type="radio"
                        name="nextTripType"
                        value={t.value}
                        checked={active}
                        onChange={handleTripTypeChange}
                        style={{ marginTop: 2 }}
                      />
                      <span>
                        <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#1e293b' }}>{t.label}</span>
                        <span style={{ display: 'block', fontSize: 11, color: '#64748b', marginTop: 2 }}>{t.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: '#64748b', margin: '8px 0 0 0' }}>
                Commencement port and date are populated from the current operation's last port.
              </p>
            </div>
          )}

          {isCargoStep && (
            <div>
              <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 6px 0', fontWeight: 500 }}>
                Select a Cargo Contract for the new voyage trip.
              </p>
              <CargoList
                cargoContracts={cargoList}
                onChangeCargoPagination={(action) =>
                  setCargoFilters((prev) => ({
                    ...prev,
                    offset:
                      action === "inc"
                        ? Math.min(prev.totalPages, prev.offset + 1)
                        : Math.max(1, prev.offset - 1),
                  }))
                }
                cargoFilters={cargoFilters}
                onClickCargo={handleCargoSelect}
                selectedCargo={selectedCargo}
                isLoading={isListLoading}
                modelType="cargo-contract-selection"
                contractType="vessel-cargo"
                onViewCargo={handleViewCargoContract}
              />
            </div>
          )}

          {currentStep === "Estimate Details" && (
            <div style={{ maxWidth: 560, margin: '0 auto', border: '1px solid #e2e8f0', padding: 12, background: '#fff' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <TextField
                  fullWidth
                  label="Estimation Date"
                  variant="outlined"
                  value={moment().format("DD-MM-YYYY")}
                  disabled
                  size="small"
                  InputProps={{ style: { fontSize: 12 } }}
                  InputLabelProps={{ style: { fontSize: 12 } }}
                />
                <TextField
                  fullWidth
                  label="Contract Name *"
                  variant="outlined"
                  name="contractName"
                  value={estimateData.contractName}
                  onChange={handleInputChange}
                  size="small"
                  required
                  InputProps={{ style: { fontSize: 12 } }}
                  InputLabelProps={{ style: { fontSize: 12 } }}
                />
                <TextField
                  fullWidth
                  label="Comments"
                  variant="outlined"
                  name="comments"
                  value={estimateData.comments}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  size="small"
                  InputProps={{ style: { fontSize: 12 } }}
                  InputLabelProps={{ style: { fontSize: 12 } }}
                />
                {!isVesselVessel && selectedCargo && (
                  <div style={{ background: '#f8fafc', padding: 8, border: '1px solid #e2e8f0' }}>
                    <p style={{ fontSize: 11, fontWeight: 500, color: '#334155', margin: '0 0 2px 0' }}>
                      Selected Cargo Summary:
                    </p>
                    <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>
                      Contract: {selectedCargo.contractName || selectedCargo.name || selectedCargo.cargoDetails?.name || "-"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>

        {/* Compact Footer Bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '6px 12px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <div>
            {activeStep > 0 && (
              <button
                onClick={handleBack}
                disabled={submitting}
                style={{ height: 28, padding: '0 14px', fontSize: 11, fontWeight: 500, color: '#1e293b', background: '#fff', border: '1px solid #cbd5e1', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: submitting ? 0.5 : 1 }}
              >
                Back
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={onClose}
              disabled={submitting}
              style={{ height: 28, padding: '0 14px', fontSize: 11, fontWeight: 500, color: '#475569', background: '#fff', border: '1px solid #cbd5e1', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: submitting ? 0.5 : 1 }}
            >
              Cancel
            </button>
            {activeStep < steps.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={isCargoStep && !selectedCargo}
                style={{ height: 28, padding: '0 14px', fontSize: 11, fontWeight: 500, color: '#fff', background: '#313C4C', border: '1px solid #313C4C', cursor: !isCargoStep || selectedCargo ? 'pointer' : 'not-allowed', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: !isCargoStep || selectedCargo ? 1 : 0.5 }}
              >
                Next Step
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ height: 28, padding: '0 14px', fontSize: 11, fontWeight: 500, color: '#fff', background: '#313C4C', border: '1px solid #313C4C', cursor: submitting ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6, opacity: submitting ? 0.5 : 1 }}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={14} color="inherit" />
                    <span>Creating...</span>
                  </>
                ) : (
                  "Create Estimate"
                )}
              </button>
            )}
          </div>
        </div>
      </Dialog>

      {/* Cargo Contract View Modal */}
      <CustomAddModal
        open={viewCargoModalOpen}
        title={"View Cargo Contract"}
        detailsTitle={"View Cargo Contract"}
        maxWidth="md"
        isDetailView={true}
        saveLabel={null}
        handleClose={() => setViewCargoModalOpen(false)}
      >
        <CreateCargoContractForm
          isReadOnly={true}
          formData={cargoFormData}
          setFormData={setCargoFormData}
          handleChange={handleCargoFormInputChange}
          removeAttachment={removeCargoAttachment}
          textContract={cargoTextContract}
          handleTextContractUpload={handleCargoTextContractUpload}
          handleAttachmentUpload={handleCargoAttachmentUpload}
          attachments={cargoAttachments}
          setTextContract={setCargoTextContract}
          isEditing={false}
          prevAttachments={prevAttachments}
          setPrevAttachments={setPrevAttachments}
        />
      </CustomAddModal>
    </>
  );
};

export default NextTripCargoSelectionModal;
